import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';
import { useToast } from '../components/layout/ToastProvider';

const { 
  FiActivity, FiTerminal, FiAlertCircle, FiInfo, FiClock, 
  FiTrash2, FiRefreshCw, FiCpu, FiShield, FiSearch, FiChevronDown
} = FiIcons;

const LOG_LEVELS = {
  error: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
  warn: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  info: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
  debug: 'bg-slate-500/10 text-slate-400 border-slate-500/20'
};

export default function DebugDashboard() {
  const { addToast } = useToast();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedLog, setExpandedLog] = useState(null);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('system_logs_2024')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      setLogs(data || []);
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const clearLogs = async () => {
    try {
      const { error } = await supabase.from('system_logs_2024').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      if (error) throw error;
      setLogs([]);
      addToast("System logs cleared");
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const filteredLogs = logs.filter(l => filter === 'all' || l.level === filter);

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-20">
      <header className="flex justify-between items-center bg-slate-900/40 backdrop-blur-3xl p-8 rounded-[2.5rem] border border-white/5">
        <div className="flex items-center gap-6">
          <div className="w-14 h-14 rounded-2xl bg-rose-500/10 flex items-center justify-center text-rose-500 border border-rose-500/20">
            <SafeIcon icon={FiTerminal} className="text-2xl" />
          </div>
          <div>
            <h1 className="text-3xl font-black text-white tracking-tighter uppercase leading-none">System Intelligence</h1>
            <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.3em] mt-2">Enterprise Monitoring Pipeline</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <button onClick={fetchLogs} className="p-4 bg-white/5 hover:bg-white/10 text-white rounded-2xl border border-white/10 transition-all">
            <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={clearLogs} className="bg-rose-600 hover:bg-rose-700 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center gap-2">
            <SafeIcon icon={FiTrash2} /> Purge Logs
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { label: 'Uptime', val: '99.98%', icon: FiActivity, color: 'text-emerald-400' },
          { label: 'Error Rate', val: '0.02%', icon: FiAlertCircle, color: 'text-rose-400' },
          { label: 'Engine Status', val: 'Operational', icon: FiCpu, color: 'text-indigo-400' }
        ].map((stat, i) => (
          <div key={i} className="bg-white/[0.02] border border-white/10 p-8 rounded-[2rem] flex items-center justify-between">
            <div>
              <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">{stat.label}</p>
              <h3 className="text-2xl font-black text-white uppercase tracking-tight">{stat.val}</h3>
            </div>
            <SafeIcon icon={stat.icon} className={`text-2xl ${stat.color}`} />
          </div>
        ))}
      </div>

      <div className="bg-white/[0.02] border border-white/10 rounded-[2.5rem] overflow-hidden">
        <div className="p-6 border-b border-white/5 flex gap-4 overflow-x-auto no-scrollbar">
          {['all', 'error', 'warn', 'info', 'debug'].map(l => (
            <button 
              key={l}
              onClick={() => setFilter(l)}
              className={`px-6 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border transition-all ${filter === l ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white/5 text-slate-500 border-white/5 hover:bg-white/10'}`}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="divide-y divide-white/5">
          {filteredLogs.map((log) => (
            <div key={log.id} className="group">
              <div 
                onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                className="p-6 flex items-center gap-6 hover:bg-white/[0.02] cursor-pointer transition-all"
              >
                <div className={`w-20 py-1 rounded-lg text-[8px] font-black uppercase text-center border ${LOG_LEVELS[log.level]}`}>
                  {log.level}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white truncate">{log.message}</p>
                </div>
                <div className="flex items-center gap-4 text-slate-500">
                  <div className="flex items-center gap-2 text-[10px] font-bold font-mono">
                    <SafeIcon icon={FiClock} />
                    {new Date(log.created_at).toLocaleTimeString()}
                  </div>
                  <SafeIcon icon={FiChevronDown} className={`transition-transform ${expandedLog === log.id ? 'rotate-180' : ''}`} />
                </div>
              </div>
              
              <AnimatePresence>
                {expandedLog === log.id && (
                  <motion.div 
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden bg-black/40"
                  >
                    <div className="p-8 border-t border-white/5 space-y-6">
                      <div className="grid grid-cols-2 gap-8">
                        <div className="space-y-4">
                          <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Environment Context</h4>
                          <div className="p-4 bg-white/5 rounded-xl space-y-2 font-mono text-[10px]">
                            <p><span className="text-indigo-400">URL:</span> {log.context.url}</p>
                            <p><span className="text-indigo-400">Agent:</span> {log.context.userAgent}</p>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Raw Payload</h4>
                          <pre className="p-4 bg-white/5 rounded-xl font-mono text-[10px] text-slate-400 overflow-x-auto">
                            {JSON.stringify(log.context, null, 2)}
                          </pre>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
          {!loading && filteredLogs.length === 0 && (
            <div className="p-20 text-center space-y-4">
              <SafeIcon icon={FiShield} className="text-4xl text-emerald-500 mx-auto" />
              <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">No anomalies detected in this channel.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}